import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  ProfileMapListRequest,
  useFetchProfileMapsQuery,
} from '../profilemap-api';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tooltip
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  MinusCircleFilled,
  MoreOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { ProfileMapViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize, DefaultPollingInterval } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import { useMasterDataDelete } from '../../masterdata/useMasterDataDelete';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function ProfileMapsScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'name': 'Name',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };

  const profilemapTable: TableState = {
    columns: [
      {
        title: 'ProfileMap',
        dataIndex: '',
        key: 'Id',
        render: (profilemap: ProfileMapViewModel) => (
          <>
              <Space>
                <span>{profilemap.id}</span>
              </Space>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'Name',
        dataIndex: '',
        key: 'name',
        render: (profilemap: ProfileMapViewModel) => (
          <><Tooltip title={profilemap.name} >
          <span>{profilemap.name}</span>
          </Tooltip>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('name'),
      },
      {
        title: 'IsBatched',
        dataIndex: '',
        key: 'isbatch',
        render: (profilemap: ProfileMapViewModel) => (
          <>
              <span>{profilemap.isBatched?'Yes':'No'}</span>
          </>
        ),
      },
      {
        title: 'BatchSize',
        key: 'batchsize',
        dataIndex: '',
        render: (profilemap: ProfileMapViewModel) => (
          <>
              <span>{profilemap.batchSize}</span>
          </>
        ),
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (profilemap: ProfileMapViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('ProfileMapEdit', {
                      tenantId: params.tenantId,
                      Id: profilemap.id,
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="delete"
                  disabled={!!(deletingId && deletingId === profilemap.id)}
                  onClick={() => deleteEntity(profilemap.id ?? 0, profilemap.name ?? '')}
                >
                  {deletingId === profilemap.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): ProfileMapListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
    };
  };

  const [pagination, setPagination] = useState<ProfileMapListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<ProfileMapViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchProfileMapsQuery(pagination, { pollingInterval: DefaultPollingInterval });

  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const {
    deletingId,
    isDeleting: isDeleteLoading,
    deleteError,
    deleteErrorMessage,
    deleteEntity,
  } = useMasterDataDelete({
    entityType: 'ProfileMaps',
    tagType: 'ProfileMap',
    entityLabel: 'profile map',
  });
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('ProfileMapEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ProfileMapViewModel> | SorterResult<ProfileMapViewModel>[],
    extra: TableCurrentDataSource<ProfileMapViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<ProfileMapViewModel>;

    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Determine new page number - ensure it's always a valid number
    let newPage = paginationConfig.current;
    if (!newPage || newPage < 1) {
      newPage = currentPage;
    }
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;

    // Check if we're actually changing sort or just pagination
    const isSortingAction = columnKey && order;
    const isCancellingSort = columnKey && !order && currentSortBy !== defaultSort;
    
    if (isSortingAction) {
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'name': 'Name',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (isCancellingSort) {
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort
      newSortBy = currentSortBy || defaultSort;
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    const newUrl = getLink(
      'ProfileMapList',
      { ...params },
      {
        sortBy: newSortBy,
        p: newPage,
        size: paginationConfig.pageSize,
        q: urlParams.get('q') || '',
      }
    );
    
    navigate(newUrl, { replace: false });
  };

  const handleSearch = (searchText: string) => {
    navigate(
      getLink(
        'ProfileMapList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          q:encodeURIComponent(searchText?.length ? `Name:*${searchText.replaceAll(" ", "\\ ")}*` : ''),
          sortBy: pagination.sortBy || '',  // Preserve current sort
        }
      )
    );
  };


  return (
    <div>
      <PageHeader
        extra={
          <Button htmlType="button" data-testid={'create-new-profilemap'} onClick={onCreateNew}>
            New Profile Map
          </Button>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Profile Maps
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="profilemaps">
              <Link to={getLink('ProfileMapList', { tenantId: params.tenantId })}>Profile Maps</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert message="Error" description={deleteErrorMessage} type="error" showIcon />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Input.Search
              placeholder="Search by Name"
              onSearch={handleSearch}
              enterButton
              style={{ width: 400 }}
              loading={isFetching}
              allowClear
            />
            <Table
            scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                ...pagination,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={profilemapTable.columns}
              loading={isFetching}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'ProfileMapType'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
